import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import { useInFlightUploads, type InFlightUploadRow } from '../../hooks/useInFlightUploads';
import { deleteSessionSnapshot } from '../FormCGIA/v2/hooks/useAutoExtractQueue';

/**
 * Contexte global des uploads CG en arrière-plan.
 *
 * Stratégie : la persistance est côté backend (table cg_in_flight_uploads
 * + WebSocket pour push). Côté frontend, ce provider ne fait que :
 *  - lire la liste via useInFlightUploads (poll + WS)
 *  - exposer des actions PATCH/DELETE pour que le wizard FormCGIAv2 mette à
 *    jour la progression au fur et à mesure
 *  - exposer createInFlight pour démarrer un nouvel upload depuis FormCGIAv2
 *
 * À monter dans main.tsx au-dessus de <Routes>, et à consommer via
 * `useBackgroundUploads()` dans le wizard + le Tracker visuel.
 */

export interface BackgroundUploadCreatePayload {
  filesTotal: number;
  payload: unknown; // snapshot du store (regPlate, department, taxData, extractedFields, uploadedDocs)
}

interface BackgroundUploadsContextValue {
  uploads: InFlightUploadRow[];
  activeCount: number;
  isLoading: boolean;
  /** Crée un nouvel in-flight côté backend. Renvoie le row créé + queued si plus de 4 actifs. */
  startUpload: (input: BackgroundUploadCreatePayload) => Promise<{ row: InFlightUploadRow; queued: boolean } | null>;
  /** PATCH partiel sur un upload (progression, état, registration_id, deal_id). */
  patchUpload: (id: string, patch: Partial<InFlightUploadRow>) => Promise<InFlightUploadRow | null>;
  /** Annulation (state=cancelled). */
  cancelUpload: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
  /** Connection WebSocket active — false = mode dégradé (polling 30s). */
  wsConnected: boolean;
  /**
   * Compteur incrémenté à chaque demande "force-show". Le tracker écoute
   * ce compteur et réinitialise son `dismissed` à false dès qu'il bouge.
   * Permet à l'utilisateur de rappeler le tracker depuis n'importe où
   * (ex: clic sur le bloc "Demandes en cours d'analyse" dans AgencyCGList).
   */
  forceShowTrackerNonce: number;
  /** Demande explicite de réouverture du tracker (incrémente forceShowTrackerNonce). */
  showTracker: () => void;
}

const BackgroundUploadsContext = createContext<BackgroundUploadsContextValue | null>(null);

export const BackgroundUploadsProvider = ({ children }: { children: ReactNode }) => {
  const { uploads, isLoading, refresh, upsertLocal, removeLocal, wsConnected } = useInFlightUploads();
  const [forceShowTrackerNonce, setForceShowTrackerNonce] = useState<number>(0);
  const showTracker = useCallback(() => setForceShowTrackerNonce((n) => n + 1), []);

  const startUpload = useCallback(
    async (input: BackgroundUploadCreatePayload) => {
      try {
        const resp = await makeAuthenticatedApiCall<{
          success: boolean;
          data: { row: InFlightUploadRow; queued: boolean };
        }>('post', '/api/cg/in-flight', input);
        const data = resp.data?.data ?? null;
        if (data) upsertLocal(data.row);
        return data;
      } catch (error) {
        console.error('BackgroundUploadsProvider / startUpload error:', error);
        return null;
      }
    },
    [upsertLocal],
  );

  // Ids dont la row backend n'existe plus (404). Cas typique : "Recommencer
  // à zéro" hard-delete la row pendant qu'une queue OCR encore active
  // continue d'envoyer sa progression via la closure capturée — sans ce
  // verrou, CHAQUE événement de progression spammait un PATCH 404.
  const deadInFlightIdsRef = useRef<Set<string>>(new Set());

  const patchUpload = useCallback(
    async (id: string, patch: Partial<InFlightUploadRow>) => {
      if (deadInFlightIdsRef.current.has(id)) return null;
      try {
        const resp = await makeAuthenticatedApiCall<{ success: boolean; data: InFlightUploadRow }>(
          'patch',
          `/api/cg/in-flight/${id}`,
          {
            state: patch.state,
            filesUploaded: patch.files_uploaded,
            filesAnalyzed: patch.files_analyzed,
            registrationId: patch.registration_id,
            dealId: patch.deal_id,
            payload: patch.payload,
            errorMessage: patch.error_message,
          },
        );
        const updated = resp.data?.data ?? null;
        if (updated) upsertLocal(updated);
        return updated;
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          // Row supprimée côté backend : on purge le fantôme local et on
          // suspend définitivement les patches de cette session.
          deadInFlightIdsRef.current.add(id);
          removeLocal(id);
          deleteSessionSnapshot(id);
          console.warn(
            `BackgroundUploadsProvider / patchUpload: in-flight ${id} introuvable (404) — patches suspendus pour cette session.`,
          );
          return null;
        }
        console.error('BackgroundUploadsProvider / patchUpload error:', error);
        return null;
      }
    },
    [upsertLocal, removeLocal],
  );

  const cancelUpload = useCallback(
    async (id: string) => {
      // 0. Suspend immédiatement tout futur PATCH de cette session — une
      //    queue OCR encore active continuerait sinon de patcher la row
      //    supprimée (404 à chaque événement de progression).
      deadInFlightIdsRef.current.add(id);
      // 1. Retire immédiatement de l'état local + localStorage (UX optimiste).
      //    Si le DELETE backend échoue, le poll suivant ré-introduira la row.
      removeLocal(id);
      // 2. Purge le snapshot in-memory de la session — libère mémoire et
      //    évite tout futur PATCH par une queue OCR encore active.
      deleteSessionSnapshot(id);
      try {
        // 3. Hard delete côté backend — supprime définitivement la row.
        await makeAuthenticatedApiCall('delete', `/api/cg/in-flight/${id}`);
      } catch (error) {
        console.error('BackgroundUploadsProvider / cancelUpload error:', error);
      }
    },
    [removeLocal],
  );

  const activeCount = useMemo(
    () =>
      uploads.filter(
        (u) =>
          u.state === 'queued' ||
          u.state === 'uploading' ||
          u.state === 'analyzing' ||
          u.state === 'submitting',
      ).length,
    [uploads],
  );

  const value = useMemo<BackgroundUploadsContextValue>(
    () => ({
      uploads,
      activeCount,
      isLoading,
      startUpload,
      patchUpload,
      cancelUpload,
      refresh,
      wsConnected,
      forceShowTrackerNonce,
      showTracker,
    }),
    [
      uploads,
      activeCount,
      isLoading,
      startUpload,
      patchUpload,
      cancelUpload,
      refresh,
      wsConnected,
      forceShowTrackerNonce,
      showTracker,
    ],
  );

  return <BackgroundUploadsContext.Provider value={value}>{children}</BackgroundUploadsContext.Provider>;
};

export const useBackgroundUploads = (): BackgroundUploadsContextValue => {
  const ctx = useContext(BackgroundUploadsContext);
  if (!ctx) {
    throw new Error('useBackgroundUploads must be used within <BackgroundUploadsProvider>');
  }
  return ctx;
};
